import { isEmpty } from 'lodash-es'
import { ComponentProps, forwardRef, useImperativeHandle, useRef } from 'react'
import { LuX } from 'react-icons/lu'

import {
  SearchItem,
  SearchResult,
  useAutoCompleteLocationSearch,
} from '@/feature/map/hooks/useAutoCompleteLocationSearch'

import styles from './LocationSearch.module.css'

export type LocationSearchProps = {
  onSearchChanged?: (search: SearchItem | undefined) => void
  onSearchUpdated: (searchTerm: string) => void
  onSearchedResults: (locations: SearchResult[]) => void
} & Omit<ComponentProps<'label'>, 'children'>

export interface LocationSearchRef {
  focus: () => void
  clear: () => void
}

export const LocationSearch = forwardRef<LocationSearchRef, LocationSearchProps>(
  ({ className, onSearchChanged, onSearchUpdated, onSearchedResults, ...props }, ref) => {
    const searchBoxElement = useRef<HTMLInputElement>(null)

    const { search, setSearch, resolveSearch } = useAutoCompleteLocationSearch({
      map: null,
      searchBoxElement,
      onSearchChanged: onSearchChanged,
      onSearchUpdated: onSearchUpdated,
      onSearchedResults: onSearchedResults,
    })

    useImperativeHandle(ref, () => ({
      focus: () => {
        searchBoxElement.current?.focus()
      },
      clear: () => {
        setSearch('')
      },
    }))

    return (
      <>
        <label {...props} className={[className, styles.searchContainer].join(' ')}>
          <div className={styles.searchIcon}>
            <div className={styles.noOverflow}>
              <button
                type="button"
                onClick={() => setSearch('')}
                className={[styles.clearButton, isEmpty(search) ? styles.empty : ''].join(' ')}
              >
                <LuX size="24" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <input
            type="text"
            placeholder="Search"
            ref={searchBoxElement}
            className={styles.searchInput}
            value={search}
            onKeyUp={resolveSearch}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </>
    )
  },
)
// Best practice for forwardRef
LocationSearch.displayName = 'LocationSearch'
